<template>
  <div class="file-inputs-container">
    <div class="file-upload">
      <div class="upload-container">
        <h6>{{ t('documentUpload.mainFile') }}</h6>
        <upload-box
          ref="mainFileInputRef"
          :button-label="t('documentUpload.uploadFileSecondary')"
          max-size="4096"
          :is-uploading="isUploading"
          :upload-progress="uploadProgress"
          @change="handleMainFileSelected"
        />
      </div>
    </div>
    <div v-if="showRenditionInput" class="file-upload">
      <div class="upload-container">
        <h6>{{ t('documentUpload.renditionFile') }}</h6>
        <upload-box
          ref="renditionInputRef"
          :button-label="t('documentUpload.uploadFileSecondary')"
          max-size="4096"
          input-id="rendition-file-input"
          :is-uploading="isUploading"
          :upload-progress="uploadProgress"
          @change="handleRenditionFileSelected"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useToast } from '@/composables/useToast';
  import { getExtensionFromFilepath } from 'utils';
  import { ref } from 'vue';
  import { onBeforeUnmount, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import UploadBox from '../UploadBox/UploadBox.vue';

  withDefaults(
    defineProps<{
      isUploading: boolean;
      uploadProgress: number | undefined;
      showRenditionInput?: boolean;
    }>(),
    {
      showRenditionInput: true,
    },
  );

  const emit = defineEmits<{
    (e: 'main-file-selected', file: File): void;
    (e: 'rendition-file-selected', file: File): void;
    (e: 'main-file-is-not-pdf'): void;
  }>();

  const { createWarningToast } = useToast();
  const { t } = useI18n();

  const mainFileInputRef = ref<InstanceType<typeof UploadBox> | null>(null);
  const renditionInputRef = ref<InstanceType<typeof UploadBox> | null>(null);

  onMounted(() => {
    window.addEventListener('dragover', preventDragDefaults);
    window.addEventListener('drop', preventDragDefaults);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('dragover', preventDragDefaults);
    window.removeEventListener('drop', preventDragDefaults);
  });

  function validateFile(file: File | undefined): file is File {
    if (!file?.size) {
      createWarningToast(t('documentUpload.warnings.fileIsEmpty'));
      return false;
    } else return true;
  }

  function handleMainFileSelected(files: File[]) {
    const file = files[0];
    if (!file) return;
    if (!validateFile(file)) return;
    if (
      renditionInputRef.value?.file &&
      getExtensionFromFilepath(file.name.toLowerCase()) !== 'pdf'
    ) {
      console.log('mainFileMustBePdf');
      createWarningToast(t('documentUpload.errors.mainFileMustBePdf'));
      mainFileInputRef.value?.resetFileInput();
      return;
    }
    emit('main-file-selected', file);
    if (getExtensionFromFilepath(file.name.toLowerCase()) !== 'pdf') {
      emit('main-file-is-not-pdf');
    }
  }

  function handleRenditionFileSelected(files: File[]) {
    const file = files[0];
    if (!file) return;
    if (!validateFile(file)) return;
    /** Rendition file must not be a PDF */
    if (getExtensionFromFilepath(file.name.toLowerCase()) === 'pdf') {
      createWarningToast(t('documentUpload.errors.renditionFileMustNotBePdf'));
      renditionInputRef.value?.resetFileInput();
      return;
    }
    emit('rendition-file-selected', file);
  }

  function preventDragDefaults(e: any) {
    e.preventDefault();
    e.stopPropagation();
  }
</script>

<style scoped lang="scss">
  .file-inputs-container {
    display: flex;
    flex-direction: row;
    column-gap: 1rem;
    .file-upload {
      .upload-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        h6 {
          font-size: 20px;
          font-weight: 700;
        }
        .vf-file-upload--dragndrop {
          row-gap: 5px;
        }
      }
    }
  }
</style>
